import styles from './DevExpressReportOptionsConf.module.scss';

import { Edit, MoreVert } from '@mui/icons-material';
import { Box, Dialog, IconButton, Popover, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapse } from '@work4all/components/lib/components/collapse';
import { BaseActionButton } from '@work4all/components/lib/input/base-action-button/BaseActionButton';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { useUser } from '@work4all/data';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { Report2 } from '@work4all/models/lib/Classes/Report2.entity';
import { ReportOptionsStruct } from '@work4all/models/lib/Classes/ReportOptionsStruct.entity';
import { ReportOptionsValue } from '@work4all/models/lib/Classes/ReportOptionsValue.entity';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';
import { ReportOptionsControl } from '@work4all/models/lib/Enums/ReportOptionsControl.enum';

import { EnumPickerField } from '../../../../../../../../../components/entity-picker/EnumPickerField';
import { settings, useSetting } from '../../../../../../../../../settings';
import { DevExpressLogoFileController } from '../../../dev-express-logo-file-controller/DevExpressLogoFileController';
import { useDevExpressReportSettings } from '../../../dev-express-logo-file-controller/hooks/use-dev-express-report-settings';
import { useDevExpressReportValues } from '../use-devexpress-report-values';

import { DevExpressFooterDialog } from './components/dev-express-footer-dialog/DevExpressFooterDialog';

export interface DevExpressReportOptionsConfProps {
  options: ReportOptionsStruct[];
  onOptionsChange: (value?: ReportOptionsValue[]) => void;
  bzObjType: BzObjType;
  report: Report2;
  reportOptionValues?: Record<string, string>;
  onChangeDevExpressReportOptionValues?: (optId: string, value: string) => void;
}

export const DevExpressReportOptionsConf = (
  props: DevExpressReportOptionsConfProps
) => {
  const {
    options = [],
    onOptionsChange,
    bzObjType,
    report,
    onChangeDevExpressReportOptionValues,
    reportOptionValues,
  } = props;
  const { t } = useTranslation();

  const changedOptions = useRef<ReportOptionsValue[]>([]);

  const user = useUser();

  const { mutate, res } = useDevExpressReportSettings();
  const [addressLineOverride, setAddressLineOverride] = useState(
    res.data.addressLineOverride
  );

  const devExpressReportOptGlobalConf = useSetting(
    settings.devExpressReportOptGlobalConf()
  );

  const { globalReportOptSettings } = useDevExpressReportValues({
    bzObjectType: bzObjType,
    report,
  });

  const { handleClick, ...popoverState } = usePopoverState('bottom');
  const [selectedOption, setSelectedOption] =
    useState<ReportOptionsStruct | null>(null);
  const [selectedOptionSettings, setSelectedOptionSettings] = useState(null);

  const handleOnAdminConfChange = useCallback(
    (
      name: string,
      key: 'defaultValue' | 'userEditAllowed',
      value: string | boolean
    ) => {
      const persistedSettings = { ...devExpressReportOptGlobalConf?.value };
      if (!persistedSettings[bzObjType]) {
        persistedSettings[bzObjType] = [];
      }

      const persistedReportConf = persistedSettings[bzObjType]?.find(
        (x) => x.reportId === report?.id
      );
      if (!persistedReportConf) {
        persistedSettings[bzObjType].push({
          reportId: report?.id,
          options: [],
        });
      }

      const persistedReportOptions = persistedSettings[bzObjType].find(
        (x) => x.reportId === report?.id
      ).options;

      let persistedReportOptConf = persistedReportOptions.find(
        (x) => x.name === name
      );
      if (!persistedReportOptConf) {
        persistedReportOptions.push({
          defaultValue: 'false',
          name: name,
          userEditAllowed: true,
        });

        persistedReportOptConf = persistedReportOptions.find(
          (x) => x.name === name
        );
      }

      persistedReportOptConf[key] = value as never;

      devExpressReportOptGlobalConf.set(persistedSettings);

      if (selectedOptionSettings && selectedOptionSettings.name === name) {
        setSelectedOptionSettings({
          ...selectedOptionSettings,
          [key]: value,
        });
      }
    },
    [
      bzObjType,
      devExpressReportOptGlobalConf,
      report?.id,
      selectedOptionSettings,
    ]
  );

  useEffect(() => {
    setAddressLineOverride(res.data.addressLineOverride);
  }, [res.data.addressLineOverride]);

  const handleAddressLineOverrideChange = useCallback(async () => {
    await mutate({
      variables: {
        addressLineOverride,
      },
    });
    onOptionsChange();
  }, [addressLineOverride, mutate, onOptionsChange]);

  const [footerDialogOpen, setFooterDialogOpen] = useState(false);

  const handleFooterDialogClose = useCallback(() => {
    setFooterDialogOpen(false);
    onOptionsChange();
  }, [onOptionsChange]);

  /**
   * Reset value when options change
   */
  useEffect(() => {
    changedOptions.current = [];
  }, [options]);

  const sortedOptions = useMemo(() => {
    return (
      [...options]?.sort((a, b) =>
        t(a.displayName).localeCompare(t(b.displayName))
      ) || []
    );
  }, [options, t]);

  return (
    <Stack>
      <Popover
        {...popoverState}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        <Box padding="0 1rem">
          <CheckboxRadioItem
            checked={selectedOptionSettings?.defaultValue === 'true'}
            label={t('REPORT.OPTIONS.SET_BY_DEFAULT')}
            onChange={(e) =>
              handleOnAdminConfChange(
                selectedOption?.name,
                'defaultValue',
                String(e.target.checked)
              )
            }
          />
        </Box>
      </Popover>

      {sortedOptions?.length > 0 && (
        <Collapse
          title={t('MASK.OPTIONS')}
          leftIndent={false}
          rightIndent={false}
        >
          {sortedOptions?.map((opt) => {
            const globalSetting = globalReportOptSettings.options?.find(
              (x) => x.name === opt.name
            ) || {
              defaultValue: 'false',
              name: opt.name,
              userEditAllowed: true,
            };

            return (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className={styles.checkboxWrap}
              >
                {opt.type === ReportOptionsControl.CHECKBOX && (
                  <>
                    <CheckboxRadioItem
                      label={t(opt.displayName)}
                      checked={reportOptionValues[opt.name] === 'true'}
                      onChange={(e) => {
                        onChangeDevExpressReportOptionValues(
                          opt.name,
                          String(e.target.checked)
                        );
                      }}
                      controlMargin={'xs'}
                      className={styles.checkbox}
                    />

                    <IconButton
                      className={styles.settingsButton}
                      size="small"
                      onClick={(e) => {
                        setSelectedOptionSettings(globalSetting);
                        setSelectedOption(opt);
                        handleClick(e);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </>
                )}
                {opt.type === ReportOptionsControl.COMBOBOX && (
                  <Box width="100%">
                    <EnumPickerField
                      values={opt.options}
                      value={
                        reportOptionValues[opt.name] || opt.defaultValue || null
                      }
                      onChange={(e) => {
                        onChangeDevExpressReportOptionValues(
                          opt.name,
                          e.toString()
                        );
                      }}
                      label={opt.displayName}
                      translationPrefix={null}
                      clearable={false}
                    />
                  </Box>
                )}
              </Stack>
            );
          })}
        </Collapse>
      )}
      {user.isMaster && (
        <>
          <Collapse title={t('COMMON.IMAGE', { count: 2 })} leftIndent={false}>
            <Stack gap="2rem">
              <DevExpressLogoFileController
                logoType="logo1"
                onChange={onOptionsChange}
              />

              <DevExpressLogoFileController
                logoType="logo2"
                onChange={onOptionsChange}
              />
            </Stack>
          </Collapse>

          <Collapse
            title={t('DEVEXPRESS_REPORT.ADDRESS_ROW')}
            leftIndent={false}
          >
            <LabeledInput
              label={t('DEVEXPRESS_REPORT.ADDRESS_ROW_OVERRIDE')}
              value={addressLineOverride}
              onChange={(e) => setAddressLineOverride(e.target.value)}
              onBlur={handleAddressLineOverrideChange}
            />
          </Collapse>

          <Collapse title={t('SIMPLE_REPORT.PAGE_FOOTER')} leftIndent={false}>
            <BaseActionButton
              icon={<Edit />}
              onClick={() => setFooterDialogOpen(true)}
            >
              {t('COMMON.EDIT')}
            </BaseActionButton>
            <Dialog open={footerDialogOpen} maxWidth="xl" fullWidth>
              <DevExpressFooterDialog onClose={handleFooterDialogClose} />
            </Dialog>
          </Collapse>
        </>
      )}
    </Stack>
  );
};
